import Ember from 'ember';
import Evented from '@ember/object/evented';
import EmberObject from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { cancel, later } from '@ember/runloop';
import { EmberRunTimer } from '@ember/runloop/types';
import { isNone } from '@ember/utils';
import { Promise } from 'rsvp';
import Bee from '@mailupinc/bee-plugin';
import ApiService from 'cio/services/api';
import RavenService from 'cio/services/raven';
import config from 'cio/config/environment';
import { BaseTemplate } from 'cio/services/editor';
import AccountService from './account';
import FeaturesService from 'ember-feature-flags/services/features';

const CSSBeeV3 = new URL('cio/assets/static-styles/bee-v3.extcss', import.meta.url).toString();

export type Template = EmberObject &
  BaseTemplate & {
    tags?: string[];
  };
type CreatePromise = {
  flag: keyof Pick<BeeService, 'isFetchingHTML' | 'isFetchingJSON' | 'isSaving'>;
  resolveAfterEvent: string;
  rejectAfterTimeout: number;
  event: () => void;
};

// Reference: https://docs.beefree.io/error-management/
type BeeErrorObject = {
  code: number;
  message: string;
  detail?: string;
};

const DEFAULT_TIMEOUT = 1000;
const DEFAULT_AJAX_TIMEOUT = 15000;

export const FETCHED_BEE_JSON = 'fetchedBeeJson';
export const FETCHED_BEE_HTML = 'fetchedBeeHtml';
export const SAVED_BEE = 'savedBee';
export const BEE_ERROR = 'beeError';
export const LOADING_BEE_TEMPLATE = 'loadingBeeTemplate';
export const LOADED_BEE_TEMPLATE = 'loadedBeeTemplate';

export class BeeTimeoutError extends Error {
  constructor(flag: CreatePromise['flag'], ...params: any[]) {
    super(...params);

    this.name = 'BeeTimeoutError';
    this.message = `Bee request timed out for ${flag}`;
  }
}

export class BeeError extends Error {
  code: number;
  detail?: string;

  constructor(error: BeeErrorObject) {
    super(`${error.code}: ${error.message}`);

    this.name = 'BeeError';
    this.code = error.code;
    this.detail = error.detail;
  }
}

const ERRORS_TO_IGNORE: Record<number, string[]> = {
  3450: ['File exceeds the limit of 1 MB'],
};

export default class BeeService extends Service.extend(Evented) {
  isFetchingJSON = false;
  isFetchingHTML = false;
  isSaving = false;

  hasLoadedContent = false;

  @service api!: ApiService;
  @service raven!: RavenService;
  @service account!: AccountService;
  @service features!: FeaturesService;

  _current?: Bee;
  _rejectPromiseTimer?: EmberRunTimer;

  get customCssUrl() {
    return CSSBeeV3.toString();
  }

  createEditor(bee = new Bee()) {
    this.set('_current', bee);
    return bee;
  }

  destroyEditor() {
    this.set('_current', null);
    this.set('hasLoadedContent', false);
  }

  getToken() {
    return this.api.request('bee_token', { pathScope: 'global' });
  }

  getCurrentInstance() {
    return this._current;
  }

  getDefaultConfig() {
    let user = this.account.current.currentUser;

    return {
      addOns: [
        {
          id: config.beeConfig.addOns.gifs.handle,
          enabled: this.features.isEnabled('bee_addon_gifs'),
        },
        {
          id: config.beeConfig.addOns.stickers.handle,
          enabled: this.features.isEnabled('bee_addon_stickers'),
        },
      ],
      advancedPermissions: {
        components: {
          filePicker: {
            maxUploadFileSize: 1 * 1024 * 1024, // 1 MB
          },
        },
      },
      editorFonts: { showDefaultFonts: true },
      language: 'en-US',
      container: 'bee-plugin-container',
      beautifyHtmlEnabled: false,
      onSaveAsTemplate: this._onSaveAsTemplate.bind(this),
      onSend: this._onSend.bind(this),
      onSave: this._onSave.bind(this),
      onError: this._onError.bind(this),
      onLoad: this._onLoad.bind(this),
      onWarning: this._onWarning.bind(this),
      customCss: this.customCssUrl,
      rowDisplayConditions: [
        {
          type: 'Example Templates',
          label: 'Trialing Customers (Example Only)',
          description: 'Only show to people with a "state" attribute set to "trialing"',
          before: "{% if customer.state == 'trialing' %}",
          after: '{% endif %}',
        },
        {
          type: 'Example Templates',
          label: 'Paying Customers (Example Only)',
          description: 'Only show to people with a "state" attribute set to "paying"',
          before: "{% if customer.state == 'paying' %}",
          after: '{% endif %}',
        },
        {
          type: 'Example Templates',
          label: 'Local Customers (Example Only)',
          description: 'Only show to people with a "postal_code" attribute set to "97205"',
          before: "{% if customer.postal_code == '97205' %}",
          after: '{% endif %}',
        },
        {
          type: 'Example Templates',
          label: 'Interested in Dogs (Example Only)',
          description: 'Only show to people with an "interests" attribute containing "dogs"',
          before: "{% if customer.interests contains 'dogs' %}",
          after: '{% endif %}',
        },
        {
          type: 'Example Templates',
          label: 'Assigned to Customer Success Manager (Example Only)',
          description: 'Only show to people with a "csm" attribute set to "Sally',
          before: "{% if customer.csm == 'Sally' %}",
          after: '{% endif %}',
        },
      ],
      username: user.fullName,
      userColor: user.uniqueColor,
    };
  }

  getJSON() {
    return this._createPromise({
      flag: 'isFetchingJSON',
      resolveAfterEvent: FETCHED_BEE_JSON,
      rejectAfterTimeout: DEFAULT_TIMEOUT,
      event: () => this.getCurrentInstance()?.saveAsTemplate(),
    });
  }

  getHTML() {
    return this._createPromise({
      flag: 'isFetchingHTML',
      resolveAfterEvent: FETCHED_BEE_HTML,
      rejectAfterTimeout: DEFAULT_AJAX_TIMEOUT,
      event: () => this.getCurrentInstance()?.send(),
    });
  }

  save() {
    return this._createPromise<{ json: string; html: string }>({
      flag: 'isSaving',
      resolveAfterEvent: SAVED_BEE,
      rejectAfterTimeout: DEFAULT_AJAX_TIMEOUT,
      event: () => this.getCurrentInstance()?.save(),
    });
  }

  _onSaveAsTemplate(json: string) {
    return this.trigger(FETCHED_BEE_JSON, json);
  }

  _onSend(html: string) {
    this.set('hasLoadedContent', true);

    return this.trigger(FETCHED_BEE_HTML, html);
  }

  _onSave(json: string, html: string) {
    return this.trigger(SAVED_BEE, { json, html });
  }

  _onLoad() {
    this.trigger(LOADED_BEE_TEMPLATE);
    return this.set('hasLoadedContent', true);
  }

  _onError(error: BeeErrorObject) {
    return this.handleError(error);
  }

  _onWarning(warning: BeeErrorObject) {
    return this.handleError(warning);
  }

  handleError(error?: BeeErrorObject) {
    if (error && error.code === 1000) {
      this.trigger(BEE_ERROR, error);

      if (!isNone(this._rejectPromiseTimer)) {
        // If there is an error thrown by Bee, save won't work, but the promise
        // rejection handler will be called and we'll show an alert. So we can
        // just cancel it ourselves.
        cancel(this._rejectPromiseTimer);
      }
    } else if (error) {
      if (this.shouldIgnoreError(error)) {
        return;
      }

      try {
        this.raven.captureException(new BeeError(error));
      } catch (_) {
        // In testing, Raven will not report the error; instead it will just
        // throw it again which will break the test. So this try...catch block
        // will ensure that the test isn't broken.
      }
    }
  }

  _createPromise<D = string>({ flag, resolveAfterEvent, rejectAfterTimeout, event }: CreatePromise) {
    return new Promise<D>((resolve, reject) => {
      this.on(resolveAfterEvent, this, (response) => {
        if (this._rejectPromiseTimer) {
          cancel(this._rejectPromiseTimer);
        }
        this.set(flag, false);
        resolve(response);
      });

      if (!Ember.testing) {
        // Reject the promise automatically so that it never gets stuck.

        this._rejectPromiseTimer = later(() => {
          reject(new BeeTimeoutError(flag));
          this.set(flag, false);
        }, rejectAfterTimeout);
      }

      this.set(flag, true);
      event();
    });
  }

  getPersistedStateFor(template: Template) {
    return localStorage.getItem(this._persistenceKeyFor(template));
  }

  persistStateFor(template: Template) {
    return this.getJSON().then((json) => {
      this._persistToLocalStorage(template, json);
    });
  }

  purgeStateFor(template: Template) {
    localStorage.removeItem(this._persistenceKeyFor(template));
  }

  _persistToLocalStorage(template: Template, json: string) {
    localStorage.setItem(this._persistenceKeyFor(template), json);
  }

  _persistenceKeyFor(template: Template) {
    return `drag-and-drop:template-${template.get('id')}`;
  }

  prepareToUpdateTemplate(triggerEvent?: boolean) {
    if (triggerEvent === true) {
      this.trigger(LOADING_BEE_TEMPLATE);
    }

    this.set('hasLoadedContent', false);
  }

  shouldIgnoreError(error: BeeErrorObject) {
    let errorMatch = ERRORS_TO_IGNORE[error.code];
    if (errorMatch != null && error.detail != null) {
      return errorMatch.includes(error.detail);
    }

    return false;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    bee: BeeService;
  }
}
